/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * boardIndexApi — the read behind /results, and behind a board's activity row.
 *
 * ONE SAVED QUERY, NOT TWO TABLE READS, and the reason is measured rather than
 * stylistic. The public table endpoint:
 *
 *   - caps a page at TEN rows and ignores page_size (200 asked, 10 returned,
 *     of a count of 20);
 *   - ignores `page` and `offset` alike — page 2 returns the same ten rows;
 *   - ignores `ordering`, so ids arrive shuffled (1, 2, 18, 7, 15, …).
 *
 * All three probed on 2026-11-02. Built on that endpoint, the index could only
 * ever show the first ten events, and a board could not find its own event
 * among twenty activities. Filters DO work there, but a board cannot filter by
 * an event id it does not yet know.
 *
 * `board_event_activities` answers every event with its activities in one go,
 * scoped 'none' and visibility 'public' like the board's other two queries, so
 * it works for a visitor with no session — which the index must, since it is
 * how somebody with a link finds a board at all.
 */

import envConfig from '../../../config/envConfig';

/** The saved query, seeded by `seed_analytics_queries`. */
export const EVENT_ACTIVITIES_QUERY = 'board_event_activities';

/** An analytics answer ({columns, rows}) as objects keyed by column name. */
export function recordsOf(payload) {
  const columns = (payload?.columns || []).map((c) => c.name);
  return (payload?.rows || []).map(
    (row) => Object.fromEntries(columns.map((c, i) => [c, row[i]])),
  );
}

/**
 * Every event and its activities, split into the two shapes the model wants.
 *
 * The query answers one row per ACTIVITY, so the events are de-duplicated here
 * — a flat join is the right shape to send over the wire and the wrong shape to
 * draw from.
 */
export async function fetchEventActivities(api, { signal } = {}) {
  const resp = await api.publicApiCall(
    `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}`
    + `/analytics/${EVENT_ACTIVITIES_QUERY}/run/`,
    { method: 'GET', signal },
  );
  if (!resp.ok) throw new Error('Could not load the boards.');
  const rows = recordsOf(await resp.json());

  const events = new Map();
  const races = [];
  rows.forEach((row) => {
    if (row.event_id === null || row.event_id === undefined) return;
    if (!events.has(String(row.event_id))) {
      events.set(String(row.event_id), {
        id: row.event_id,
        title: row.event_title,
        // The card's picture and blurb. The blurb arrives already cut to 400
        // characters by the saved query — a description can run to thousands,
        // and this one read answers for every event at once.
        cover_image_url: row.cover_image_url,
        description: row.description,
        location: row.location,
        start_datetime: row.start_datetime,
        end_datetime: row.end_datetime,
      });
    }
    if (row.activity_id !== null && row.activity_id !== undefined) {
      races.push({
        id: row.activity_id,
        name: row.activity_name,
        event_id: row.event_id,
        order_index: row.order_index,
        results_published: row.results_published,
      });
    }
  });

  return { events: [...events.values()], races };
}
